/**
 * @file TemplateElementLines.tsx
 * @description Элементы блока шаблона страницы товара строками под блоком
 * в структуре (phase19.16 Р2а; uiux-design-r2a §3 «· Галерея · Название ·
 * Цена и покупка (з)»): нажатие открывает элемент в панели; у обязательных
 * — замок (з). На касании строка 48, на ПК — компактнее.
 */

import type { SectionPayload } from "@chaiberi/shared";
import { Lock } from "lucide-react";

import { useBuilder } from "../builder-context";
import { isSectionBlock } from "../../site-pages/block-meta";
import type { SitePageBlock } from "../../../lib/api-site-pages";
import { templateElementLines } from "../../../lib/site-builder/template-rules";
import { cn } from "@/lib/utils";

export function TemplateElementLines({ block }: { block: SitePageBlock }) {
  const b = useBuilder();
  if (!isSectionBlock(block)) return null;
  const lines = templateElementLines(block.payload as SectionPayload);
  if (lines.length === 0) return null;
  const selected = b.element?.blockId === block.id ? b.element.elementId : null;

  async function open(elementId: string) {
    const moved = await b.select({ sel: { kind: "block", id: block.id }, panel: null });
    if (moved) b.setElement({ blockId: block.id, elementId });
  }

  return (
    <ul className="flex flex-col" aria-label="Элементы блока">
      {lines.map((line) => (
        <li key={line.id}>
          <button
            type="button"
            onClick={() => void open(line.id)}
            aria-current={selected === line.id ? "true" : undefined}
            className={cn(
              "type-caption flex min-h-12 w-full items-center gap-2 rounded-md px-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring xl:min-h-8",
              selected === line.id ? "bg-primary/10 text-on-surface" : "text-on-surface-variant hover:bg-surface-low",
            )}
          >
            <span aria-hidden>·</span>
            <span className="min-w-0 flex-1 truncate">{line.label}</span>
            {line.required ? <Lock aria-label="Обязательный элемент" className="size-3.5 shrink-0" /> : null}
          </button>
        </li>
      ))}
    </ul>
  );
}
